import { Trash2 } from "lucide-react";
import { useEffect, useRef, useState, type RefObject } from "react";

import { fmtDisplay } from "@/lib/date";
import { cn } from "@/lib/utils";

import { DrillCalendar } from "./DrillCalendar";
import { MonthPanel } from "./MonthPanel";
import type { DrillState } from "./types";

interface CalendarPopoverProps {
  /** Range mode: both dates picked → calls onConfirm with [start, end]. */
  startDate: Date | null;
  endDate: Date | null;
  /** True for the dual-month picker (range), false for single-month single-date. */
  rangeMode: boolean;
  stacked: boolean;
  firstNavRef?: RefObject<HTMLButtonElement>;
  onConfirm: (start: Date | null, end: Date | null) => void;
  onCancel: () => void;
  onClear: () => void;
}

/** Internal primitive shared by DatePicker and DateRangePicker. */
export function CalendarPopover({
  startDate,
  endDate,
  rangeMode,
  stacked,
  firstNavRef,
  onConfirm,
  onCancel,
  onClear,
}: CalendarPopoverProps) {
  const today = new Date();
  const init = startDate ?? today;
  const fallbackRef = useRef<HTMLButtonElement | null>(null);
  const navRef = firstNavRef ?? fallbackRef;

  const [leftYear, setLeftYear] = useState(init.getFullYear());
  const [leftMonth, setLeftMonth] = useState(init.getMonth());
  const [selStart, setSelStart] = useState<Date | null>(startDate);
  const [selEnd, setSelEnd] = useState<Date | null>(endDate);
  const [hoverDate, setHoverDate] = useState<Date | null>(null);
  const [picking, setPicking] = useState<"start" | "end">(
    rangeMode ? "start" : "start",
  );
  const [drill, setDrill] = useState<DrillState | null>(null);

  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  const nav = (md: number, yd = 0) => {
    let m = leftMonth + md;
    let y = leftYear + yd;
    while (m > 11) { m -= 12; y++; }
    while (m < 0)  { m += 12; y--; }
    setLeftMonth(m);
    setLeftYear(y);
  };

  // Esc closes the popover.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCancel();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const handleDayClick = (date: Date) => {
    if (!rangeMode) {
      setSelStart(date);
      onConfirm(date, null);
      return;
    }
    if (picking === "start" || (selStart && selEnd)) {
      setSelStart(date);
      setSelEnd(null);
      setHoverDate(null);
      setPicking("end");
    } else {
      if (selStart && date < selStart) {
        setSelEnd(selStart);
        setSelStart(date);
      } else {
        setSelEnd(date);
      }
      setPicking("start");
    }
  };

  const handleClear = () => {
    setSelStart(null);
    setSelEnd(null);
    setHoverDate(null);
    setPicking("start");
    onClear();
  };

  const common = {
    startDate: selStart,
    endDate: rangeMode ? selEnd : null,
    hoverDate: rangeMode ? hoverDate : null,
    onDayClick: handleDayClick,
    onDayHover: rangeMode ? setHoverDate : () => undefined,
  };

  if (drill) {
    return (
      <div
        className={cn(
          "rounded-2xl bg-popover p-4 shadow-xl ring-1 ring-border",
          stacked ? "w-[320px]" : rangeMode ? "w-[660px]" : "w-[320px]",
        )}
      >
        <DrillCalendar
          initialYear={drill.year}
          initialMonth={drill.month}
          onSelectDate={(date) => {
            if (drill.side === "left" || !rangeMode) {
              setLeftYear(date.getFullYear());
              setLeftMonth(date.getMonth());
            } else {
              let m = date.getMonth() - 1;
              let y = date.getFullYear();
              if (m < 0) { m = 11; y--; }
              setLeftYear(y);
              setLeftMonth(m);
            }
            setDrill(null);
          }}
          onClose={() => setDrill(null)}
        />
      </div>
    );
  }

  const footer = (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 px-5 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-semibold text-brand">
        {selStart && <span>{fmtDisplay(selStart)}</span>}
        {rangeMode && selStart && <span className="font-normal text-muted-foreground">→</span>}
        {rangeMode && selEnd && <span>{fmtDisplay(selEnd)}</span>}
        {rangeMode && selStart && !selEnd && (
          <span className="text-xs font-normal text-muted-foreground">
            Select end date…
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleClear}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-destructive hover:bg-destructive/5 hover:text-destructive"
        >
          <Trash2 className="h-3 w-3" />
          Reset
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-[13px] font-medium text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        {rangeMode && (
          <button
            type="button"
            disabled={!(selStart && selEnd)}
            onClick={() => selStart && selEnd && onConfirm(selStart, selEnd)}
            className={cn(
              "rounded-lg px-5 py-2 text-[13px] font-bold text-white transition-colors",
              selStart && selEnd
                ? "bg-brand hover:bg-brand-hover"
                : "cursor-not-allowed bg-muted-foreground/40",
            )}
          >
            Confirm
          </button>
        )}
      </div>
    </div>
  );

  const navBar = (
    <div className="flex items-center justify-between px-5 pb-1.5 pt-3.5">
      <div className="flex gap-1">
        <button
          ref={navRef}
          type="button"
          onClick={() => nav(0, -1)}
          title="Prev year"
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          «
        </button>
        <button
          type="button"
          onClick={() => nav(-1)}
          title="Prev month"
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          ‹
        </button>
      </div>
      <div className="flex gap-1">
        <button
          type="button"
          onClick={() => nav(1)}
          title="Next month"
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => nav(0, 1)}
          title="Next year"
          className="rounded-md px-2 py-1 text-[17px] text-brand hover:bg-accent"
        >
          »
        </button>
      </div>
    </div>
  );

  // Stacked (narrow) layout.
  if (stacked) {
    return (
      <div
        role="dialog"
        aria-label="Date picker"
        className="w-[320px] select-none overflow-hidden rounded-2xl bg-popover shadow-xl ring-1 ring-border"
      >
        {navBar}
        <div className="px-5 pb-3 pt-1">
          <MonthPanel
            {...common}
            year={leftYear}
            month={leftMonth}
            onDrillDown={(y, m) => setDrill({ year: y, month: m, side: "left" })}
          />
        </div>
        {rangeMode && (
          <>
            <div className="mx-5 h-px bg-border/60" />
            <div className="px-5 pb-3 pt-3">
              <MonthPanel
                {...common}
                year={rightYear}
                month={rightMonth}
                onDrillDown={(y, m) => setDrill({ year: y, month: m, side: "right" })}
              />
            </div>
          </>
        )}
        {footer}
      </div>
    );
  }

  // Side-by-side range mode.
  if (rangeMode) {
    return (
      <div
        role="dialog"
        aria-label="Date range picker"
        className="w-[660px] select-none overflow-hidden rounded-2xl bg-popover shadow-xl ring-1 ring-border"
      >
        {navBar}
        <div className="flex">
          <div className="flex-1 px-5 pb-3 pt-1">
            <MonthPanel
              {...common}
              year={leftYear}
              month={leftMonth}
              onDrillDown={(y, m) => setDrill({ year: y, month: m, side: "left" })}
            />
          </div>
          <div className="my-1 mb-3 w-px bg-border/60" />
          <div className="flex-1 px-5 pb-3 pt-1">
            <MonthPanel
              {...common}
              year={rightYear}
              month={rightMonth}
              onDrillDown={(y, m) => setDrill({ year: y, month: m, side: "right" })}
            />
          </div>
        </div>
        {footer}
      </div>
    );
  }

  // Single-month layout for single-date mode.
  return (
    <div
      role="dialog"
      aria-label="Date picker"
      className="w-[320px] select-none overflow-hidden rounded-2xl bg-popover shadow-xl ring-1 ring-border"
    >
      {navBar}
      <div className="px-5 pb-3 pt-1">
        <MonthPanel
          {...common}
          year={leftYear}
          month={leftMonth}
          onDrillDown={(y, m) => setDrill({ year: y, month: m, side: "left" })}
        />
      </div>
      {footer}
    </div>
  );
}
